// /partner/accounting — Accounting export: Xero-ready CSVs that also import into QuickBooks and FreeAgent.
import React, { useEffect, useState } from "react";
import { Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Card, Stat, Loading, inp, gbp } from "@/components/kit";

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
const lastMonth = () => {
  const n = new Date();
  return [ymd(new Date(n.getFullYear(), n.getMonth() - 1, 1)), ymd(new Date(n.getFullYear(), n.getMonth(), 0))];
};

export default function PartnerAccounting() {
  const [[start, end], setRange] = useState(lastMonth);
  const [vat, setVat] = useState(0);
  const [account, setAccount] = useState("200");
  const [sum, setSum] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    setSum(null);
    api("/partner/accounting/summary?" + new URLSearchParams({ start, end })).then((d) => setSum(d)).catch(() => setSum(false));
  }, [start, end]);

  const download = async (kind) => {
    setBusy(kind);
    try {
      const text = await api("/partner/accounting/export?" + new URLSearchParams({ kind, start, end, vat: String(vat), account }));
      const url = URL.createObjectURL(new Blob([typeof text === "string" ? text : ""], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "fable-plus-" + kind + "-" + start + "-to-" + end + ".csv";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch {
      toast.error("Couldn't build that file — check the dates and try again.");
    } finally { setBusy(""); }
  };

  return (
    <div className="space-y-5" data-testid="partner-accounting">
      <div>
        <h1 className="text-2xl font-bold">Accounting export</h1>
        <p className="mt-1 text-sm text-muted-foreground">Download your bookings as files your accounting software imports — built for Xero, and they work in QuickBooks and FreeAgent too.</p>
      </div>
      <Card title="Period">
        <div className="grid gap-3 sm:grid-cols-4">
          <label className="text-sm">From<input type="date" className={inp + " mt-1"} value={start} max={end} onChange={(e) => setRange([e.target.value, end])} data-testid="acc-start" /></label>
          <label className="text-sm">To<input type="date" className={inp + " mt-1"} value={end} min={start} onChange={(e) => setRange([start, e.target.value])} data-testid="acc-end" /></label>
          <label className="text-sm">VAT on your sales
            <select className={inp + " mt-1"} value={vat} onChange={(e) => setVat(Number(e.target.value))} data-testid="acc-vat">
              <option value={0}>Not VAT registered</option>
              <option value={20}>20% VAT on income</option>
            </select>
          </label>
          <label className="text-sm">Sales account code<input className={inp + " mt-1"} value={account} maxLength={10} onChange={(e) => setAccount(e.target.value.replace(/[^A-Za-z0-9]/g, ""))} data-testid="acc-account" /></label>
        </div>
      </Card>
      {sum === null ? <Loading /> : sum === false ? <p className="text-sm text-muted-foreground">Couldn't load your totals.</p> : (
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Stat k="Bookings" v={sum.bookings} />
          <Stat k="Sales" v={gbp(sum.sales)} />
          <Stat k="Tips" v={gbp(sum.tips)} />
          <Stat k="Paid by card" v={gbp(sum.card_sales)} />
          <Stat k="Paid in cash" v={gbp(sum.cash_sales)} />
          <Stat k="Fable+ commission" v={gbp(sum.commission)} />
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        <Card title="Sales invoices" right={<FileSpreadsheet size={18} />}>
          <p className="text-sm text-muted-foreground">One invoice per booking, with a separate line for tips. In Xero: Business → Invoices → Import.</p>
          <Button className="mt-4" disabled={!!busy} onClick={() => download("sales")} data-testid="acc-download-sales"><Download className="mr-1 h-4 w-4" />{busy === "sales" ? "Building…" : "Download sales CSV"}</Button>
        </Card>
        <Card title="Fees statement" right={<FileSpreadsheet size={18} />}>
          <p className="text-sm text-muted-foreground">Fable+ commission on card bookings, as money out. In Xero: your bank account → Import a statement.</p>
          <Button className="mt-4" variant="outline" disabled={!!busy} onClick={() => download("fees")} data-testid="acc-download-fees"><Download className="mr-1 h-4 w-4" />{busy === "fees" ? "Building…" : "Download fees CSV"}</Button>
        </Card>
      </div>
      <p className="text-xs text-muted-foreground">Customer emails are never included — only the name each customer shows on Fable+. Your plan subscription invoices come from Stripe by email.</p>
    </div>
  );
}
